import { useEffect, useState } from 'react';
import useStoreFilter from './useStoreFilter';
import { Alert, message } from 'antd';
import TiktokPerformanceTable from './TiktokPerformanceTable';
import { useRequest } from 'ahooks';
import { getStorePerfList } from './service';
import { useTranslate } from '@tolgee/react';
import { Container } from './ShopeePerformance';
import { eventBus } from 'services/eventBus';
import useModal from 'hooks/useModal';
import TiktokTrendDrawer from './TiktokTrendDrawer';
import dayjs from 'dayjs';

function TiktokPerformance({
  platform,
  departments,
}: {
  platform: 'TIKTOK' | 'TIKTOK_US';
  departments: Dashboard.Department[];
}) {
  const { t } = useTranslate();

  const {
    selectedDepartments,
    departmentsSelect,
    storeType,
    storeTypeSelect,
    storeInfo,
    storeInfoSelect,
  } = useStoreFilter({
    platform,
    departments,
  });

  const filter = (
    <header>
      {departmentsSelect}
      {storeTypeSelect}
      {storeInfoSelect}
    </header>
  );

  const dateRender = (x: number) => dayjs().subtract(x, 'days').format('YYYY/MM/DD');

  const banner = (
    <Alert
      banner
      showIcon
      type="info"
      message={
        <div>
          {t('数据统计区间：x-y', {
            x: dateRender(31),
            y: dateRender(2),
          })}
        </div>
      }
    />
  );

  const checkDetail = ({
    storeId,
    region,
    storeName,
    storeAlias,
  }: Dashboard.PlatformData.StorePerfData) => {
    eventBus.emit('CHECK_STORE_PLATFORM_CSAT_DETAIL', {
      id: storeId as any,
      platform,
      region,
      storeName,
      storeNameAlias: storeAlias,
      storeType: 'LOCAL',
    });
  };

  const { visible, show, hide, meta } = useModal<Dashboard.PlatformData.StorePerfData>();

  const drawer = meta && (
    <TiktokTrendDrawer
      key={meta.storeId}
      platform={platform}
      visible={visible}
      onClose={() => hide()}
      storePerfData={meta}
      onCheckDetail={checkDetail}
    />
  );

  const [sortKey, setSortKey] = useState<string>();
  const [sortType, setSortType] = useState<1 | 2>();

  const { loading, data, run } = useRequest(
    (
      page: {
        pageNum: number;
        pageSize: number;
      } = {
        pageNum: 1,
        pageSize: 10,
      },
    ) => {
      return getStorePerfList({
        ...page,
        sortKey,
        sortType,
        platform,
        departmentIds: selectedDepartments.map(({ id }) => id),
        storeType: storeType === 'CROSS' ? '跨境' : storeType === 'LOCAL' ? '本土' : undefined,
        storeIds: storeInfo.list?.map(({ id }) => Number(id)),
      });
    },
    {
      manual: true,
      onError: () => message.error(t('phrase.获取数据失败，请稍后再试')),
    },
  );

  useEffect(() => {
    run();
  }, [run, sortKey, sortType, selectedDepartments, storeType, storeInfo]);

  const table = (
    <TiktokPerformanceTable
      loading={loading}
      dataSource={data?.list}
      pagination={{
        current: data?.pageNum || 1,
        pageSize: data?.pageSize || 10,
        total: data?.total,
        showSizeChanger: true,
        showQuickJumper: true,
        showTotal: (x) => t('feedback.共x项', { x }),
      }}
      onChange={(page: any, _, { order, field }: any) => {
        if (page.current === (data?.pageNum || 1) && page.pageSize === (data?.pageSize || 10)) {
          setSortKey(order && field);
          setSortType(order === 'ascend' ? 1 : order === 'descend' ? 2 : undefined);
        } else {
          run({
            pageNum: page.current,
            pageSize: page.pageSize,
          });
        }
      }}
      onCheckDetail={checkDetail}
      onCheckTrend={show}
    />
  );

  return (
    <Container>
      {filter}
      {banner}
      <section>{table}</section>
      {drawer}
    </Container>
  );
}

export default TiktokPerformance;
